import {useCallback, useEffect, useState} from "react";
import {useSelector} from "react-redux";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import StatusBadge from "@/components/StatusBadge";
import ChoiceChips from "@/components/ChoiceChips";
import EmptyData from "@/components/EmptyData";
import VLFCard from "@/components/Card";
import Kpi, {KpiRow} from "@/components/Kpi";
import DataTable from "@/components/DataTable";
import VLFModal from "@/components/Modal";
import VLFTextField from "@/components/Input/TextField";
import VLFSelectNew from "@/components/Input/SelectNew";

import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import {RosterAPI} from "@/api/roster";
import {visibleRosters} from "@/utils/functions";
import {ContractAPI} from "@/api/contract";
import {PlayerAPI} from "@/api/player";

import {
	AREA_OPTIONS,
	ERROR_OPERATION,
	getInternalClubId,
} from "@/utils/constants";

import "./index.scss";

// ── Constants ─────────────────────────────────────────────────────────────────
const LS_KEY = "ms_scenarios_v1";
const PROSPECT_PAGE_SIZE = 15;

// Prossime tre finestre di mercato da oggi: inverno a gennaio, estate a luglio-agosto
const buildWindows = () => {
	const today = moment();
	const windows = [];
	for (let year = today.year(); windows.length < 3; year++) {
		if (today.isSameOrBefore(moment({year, month: 0}).endOf("month"))) {
			windows.push({
				value: "inverno_" + year,
				label: `Inverno ${year} (gennaio)`,
			});
		}
		if (
			windows.length < 3 &&
			today.isSameOrBefore(moment({year, month: 7}).endOf("month"))
		) {
			windows.push({
				value: "estate_" + year,
				label: `Estate ${year} (luglio-agosto)`,
			});
		}
	}
	return windows;
};

const WINDOWS = buildWindows();
const DEFAULT_WINDOW = WINDOWS[0].value;

// Per gli scenari salvati con finestre passate: "estate_2025" → "Estate 2025"
const windowLabel = (value) => {
	const known = WINDOWS.find((w) => w.value === value);
	if (known) return known.label;
	if (!value) return "";
	const [period, year] = value.split("_");
	return (
		period.charAt(0).toUpperCase() +
		period.slice(1) +
		" " +
		(year || "")
	).trim();
};

const DEPARTURE_REASONS = [
	{value: "cessione", label: "Cessione"},
	{value: "fine_contratto", label: "Fine contratto"},
	{value: "prestito", label: "Prestito"},
	{value: "svincolo", label: "Svincolo"},
];

const ROLE_OPTIONS = AREA_OPTIONS.map((o) => ({
	value: o.label,
	label: o.label,
}));

const CONTRACT_TONES = {
	expired: "critical",
	critical: "critical",
	warning: "warning",
};

// ── localStorage helpers ──────────────────────────────────────────────────────
const loadScenarios = () => {
	try {
		return JSON.parse(localStorage.getItem(LS_KEY)) || [];
	} catch {
		return [];
	}
};

const saveScenarios = (scenarios) => {
	localStorage.setItem(LS_KEY, JSON.stringify(scenarios));
};

const newId = () => crypto.randomUUID();

// ── Helpers ───────────────────────────────────────────────────────────────────
const getPlayerRoleGroup = (player) => {
	if (player?.goalkeeper) return "Portieri";
	const opt = AREA_OPTIONS.find((o) => o.label === player?.primaryRole);
	return opt?.groupLabel || "Altri";
};

const contractMonths = (endDate) => {
	if (!endDate) return null;
	return moment(endDate).diff(moment(), "months");
};

const contractStatus = (endDate) => {
	if (!endDate) return "none";
	const m = contractMonths(endDate);
	if (m < 0) return "expired";
	if (m <= 12) return "critical";
	if (m <= 24) return "warning";
	return "safe";
};

const isExpiringSoon = (contract) => {
	if (!contract?.endDate) return false;
	return moment(contract.endDate).isBefore(moment().add(6, "months"));
};

const isAlreadyExpired = (contract) => {
	if (!contract?.endDate) return false;
	return moment(contract.endDate).isBefore(moment());
};

const fmtSalary = (n) => {
	if (!n && n !== 0) return null;
	return "€ " + Number(n).toLocaleString("it-IT");
};

// ── Role breakdown ────────────────────────────────────────────────────────────
const computeRoleBreakdown = (players, departures, arrivals) => {
	const rows = AREA_OPTIONS.map((opt) => {
		const current = players.filter(
			(p) =>
				(p.player?.goalkeeper && opt.value === "Goalkeeper") ||
				p.player?.primaryRole === opt.label,
		).length;
		const out = departures.filter(
			(d) => d.groupLabel === opt.groupLabel,
		).length;
		const inCount = arrivals.filter(
			(a) =>
				(a.isGoalkeeper && opt.value === "Goalkeeper") || a.role === opt.label,
		).length;
		return {
			groupLabel: opt.groupLabel,
			current,
			out,
			in: inCount,
			simulated: current - out + inCount,
		};
	});
	return rows.filter((r) => r.current > 0 || r.in > 0);
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// MAIN PAGE
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function MarketSimulationPage({handleNotification}) {
	const seasonId = useSelector((state) => state?.season?.id);

	// ── Persistent state ──────────────────────────────────────────────────────
	const [scenarios, setScenarios] = useState(loadScenarios);
	const [activeId, setActiveId] = useState(
		() => loadScenarios()[0]?.id || null,
	);

	// ── Remote data ───────────────────────────────────────────────────────────
	const [loading, setLoading] = useState(false);
	const [teams, setTeams] = useState([]);
	const [contractsMap, setContractsMap] = useState({});
	const [currentTeam, setCurrentTeam] = useState(null);
	const [prospectPlayers, setProspectPlayers] = useState([]);
	const [prospectsLoading, setProspectsLoading] = useState(false);
	const [prospectPage, setProspectPage] = useState(1);

	// ── UI state ──────────────────────────────────────────────────────────────
	const [showNewModal, setShowNewModal] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [showProspectModal, setShowProspectModal] = useState(false);
	const [prospectSearch, setProspectSearch] = useState("");
	const [showManualForm, setShowManualForm] = useState(false);
	const [manualForm, setManualForm] = useState({
		name: "",
		role: "",
		salary: "",
	});
	const [newForm, setNewForm] = useState({
		name: "",
		teamId: "",
		window: DEFAULT_WINDOW,
	});

	// ── Derived: active scenario ──────────────────────────────────────────────
	const activeScenario = scenarios.find((s) => s.id === activeId) || null;

	// ── Persist on every change ───────────────────────────────────────────────
	const updateScenarios = useCallback((next) => {
		setScenarios(next);
		saveScenarios(next);
	}, []);

	const patchActive = useCallback(
		(patch) => {
			updateScenarios(
				scenarios.map((s) => (s.id === activeId ? {...s, ...patch} : s)),
			);
		},
		[scenarios, activeId, updateScenarios],
	);

	// ── Load rosters + contracts ──────────────────────────────────────────────
	useEffect(() => {
		if (!seasonId) return;
		const load = async () => {
			setLoading(true);
			// Ogni chiamata gestisce il proprio errore: se una fallisce l'altra resta
			let failed = false;
			const onError = () => {
				failed = true;
				return [];
			};
			try {
				const [teamsRes, contractsRes] = await Promise.all([
					RosterAPI.list({
						seasonId,
						prospect: false,
						clubId: getInternalClubId(),
					}).catch(onError),
					ContractAPI.expiring(36500).catch(onError),
				]);
				const internal = visibleRosters(
					(teamsRes || []).filter(
						(r) =>
							!r.prospect &&
							!r.playerCategory?.basicActivities &&
							(!r.clubId || r.clubId === getInternalClubId()),
					),
				);
				setTeams(internal);
				const map = {};
				(contractsRes || []).forEach((c) => {
					if (c.playerId) map[c.playerId] = c;
				});
				setContractsMap(map);
				if (failed) handleNotification(ERROR_OPERATION, "error");
			} catch {
				handleNotification(ERROR_OPERATION, "error");
			} finally {
				setLoading(false);
			}
		};
		load();
	}, [seasonId]);

	// ── Load all prospects once when modal opens ──────────────────────────────
	useEffect(() => {
		if (!showProspectModal || prospectPlayers.length > 0) return;
		const load = async () => {
			setProspectsLoading(true);
			try {
				const res = await PlayerAPI.list({prospect: true, seasonId});
				const sorted = (res || []).sort((a, b) =>
					(a.lastName || "").localeCompare(b.lastName || ""),
				);
				setProspectPlayers(sorted);
			} catch {
				handleNotification(ERROR_OPERATION, "error");
			} finally {
				setProspectsLoading(false);
			}
		};
		load();
	}, [showProspectModal]);

	// ── Load team detail ──────────────────────────────────────────────────────
	useEffect(() => {
		if (!activeScenario?.teamId) {
			setCurrentTeam(null);
			return;
		}
		const load = async () => {
			setLoading(true);
			try {
				const res = await RosterAPI.getById(activeScenario.teamId);
				setCurrentTeam(res);
			} catch {
				handleNotification(ERROR_OPERATION, "error");
			} finally {
				setLoading(false);
			}
		};
		load();
	}, [activeScenario?.teamId]);

	// ── Computed values ───────────────────────────────────────────────────────
	const players = currentTeam?.players || [];
	const departures = activeScenario?.departures || [];
	const arrivals = activeScenario?.arrivals || [];
	const departureSet = new Set(departures.map((d) => d.playerId));

	const currentMonthlySalary = players.reduce((acc, p) => {
		return acc + (contractsMap[p.player?.id]?.totalMonthlySalary || 0);
	}, 0);
	const departureSalary = departures.reduce(
		(acc, d) => acc + (d.monthlySalary || 0),
		0,
	);
	const arrivalSalary = arrivals.reduce(
		(acc, a) => acc + (Number(a.monthlySalary) || 0),
		0,
	);
	const simulatedMonthlySalary =
		currentMonthlySalary - departureSalary + arrivalSalary;
	const salaryDelta = simulatedMonthlySalary - currentMonthlySalary;

	const roleBreakdown = computeRoleBreakdown(players, departures, arrivals);

	const expiringCount = players.filter((p) => {
		const c = contractsMap[p.player?.id];
		return c && isExpiringSoon(c) && !departureSet.has(p.player?.id);
	}).length;

	const avgAge = (() => {
		const ages = players
			.map((p) =>
				p.player?.dateOfBirth
					? moment().diff(moment(p.player.dateOfBirth), "years")
					: null,
			)
			.filter(Boolean);
		return ages.length
			? (ages.reduce((s, a) => s + a, 0) / ages.length).toFixed(1)
			: null;
	})();

	// ── Grouped players ───────────────────────────────────────────────────────
	const groupedPlayers = {};
	AREA_OPTIONS.forEach((opt) => {
		const members = players.filter(
			(p) =>
				(p.player?.goalkeeper && opt.value === "Goalkeeper") ||
				p.player?.primaryRole === opt.label,
		);
		if (members.length) groupedPlayers[opt.groupLabel] = members;
	});

	// ── Options ───────────────────────────────────────────────────────────────
	const teamOptions = teams.map((t) => ({
		value: t.id,
		label: t.name || t.playerCategory?.name || t.id,
	}));

	const scenarioOptions = scenarios.map((s) => ({value: s.id, label: s.name}));

	const filteredProspects = prospectPlayers.filter((p) => {
		if (!prospectSearch) return true;
		const full = `${p.lastName || ""} ${p.firstName || ""}`.toLowerCase();
		return full.includes(prospectSearch.toLowerCase());
	});
	const prospectTotalPages = Math.ceil(
		filteredProspects.length / PROSPECT_PAGE_SIZE,
	);
	const pagedProspects = filteredProspects.slice(
		(prospectPage - 1) * PROSPECT_PAGE_SIZE,
		prospectPage * PROSPECT_PAGE_SIZE,
	);

	// ── Modali ──────────────────────────────────────────────────────────────
	const closeProspectModal = () => {
		setShowProspectModal(false);
		setProspectSearch("");
		setProspectPage(1);
	};

	const closeNewModal = () => {
		setShowNewModal(false);
		setNewForm({name: "", teamId: "", window: DEFAULT_WINDOW});
	};

	// ── Scenario CRUD ─────────────────────────────────────────────────────────
	const handleCreateScenario = () => {
		if (!newForm.name || !newForm.teamId) return;
		const scenario = {
			id: newId(),
			name: newForm.name,
			teamId: newForm.teamId,
			window: newForm.window,
			createdAt: new Date().toISOString(),
			departures: [],
			arrivals: [],
		};
		updateScenarios([...scenarios, scenario]);
		setActiveId(scenario.id);
		setShowNewModal(false);
		setNewForm({name: "", teamId: "", window: DEFAULT_WINDOW});
	};

	const handleDeleteScenario = () => {
		const next = scenarios.filter((s) => s.id !== activeId);
		updateScenarios(next);
		setActiveId(next[0]?.id || null);
		setShowDeleteModal(false);
	};

	// ── Departures ────────────────────────────────────────────────────────────
	const toggleDeparture = (player) => {
		const pid = player.player?.id;
		if (departureSet.has(pid)) {
			patchActive({departures: departures.filter((d) => d.playerId !== pid)});
		} else {
			const contract = contractsMap[pid];
			patchActive({
				departures: [
					...departures,
					{
						playerId: pid,
						playerName: `${player.player?.lastName} ${player.player?.firstName}`,
						role:
							player.player?.primaryRole ||
							(player.player?.goalkeeper ? "Portiere" : "—"),
						isGoalkeeper: !!player.player?.goalkeeper,
						groupLabel: getPlayerRoleGroup(player.player),
						monthlySalary: contract?.totalMonthlySalary || 0,
						reason: "cessione",
					},
				],
			});
		}
	};

	const setDepartureReason = (playerId, reason) => {
		patchActive({
			departures: departures.map((d) =>
				d.playerId === playerId ? {...d, reason} : d,
			),
		});
	};

	const removeDeparture = (playerId) => {
		patchActive({
			departures: departures.filter((d) => d.playerId !== playerId),
		});
	};

	// ── Arrivals ──────────────────────────────────────────────────────────────
	const addProspectArrival = (prospect) => {
		if (arrivals.some((a) => a.prospectId === prospect.id)) return;
		const opt = AREA_OPTIONS.find((o) => o.label === prospect.primaryRole);
		patchActive({
			arrivals: [
				...arrivals,
				{
					id: newId(),
					name: `${prospect.lastName} ${prospect.firstName}`,
					role: prospect.primaryRole || "—",
					isGoalkeeper: !!prospect.goalkeeper,
					groupLabel: opt?.groupLabel || "Altri",
					monthlySalary: 0,
					source: "scouting",
					prospectId: prospect.id,
				},
			],
		});
		closeProspectModal();
	};

	const addManualArrival = () => {
		if (!manualForm.name || !manualForm.role) return;
		const opt = AREA_OPTIONS.find((o) => o.label === manualForm.role);
		patchActive({
			arrivals: [
				...arrivals,
				{
					id: newId(),
					name: manualForm.name,
					role: manualForm.role,
					isGoalkeeper: manualForm.role === "Portiere",
					groupLabel: opt?.groupLabel || "Altri",
					monthlySalary: Number(manualForm.salary) || 0,
					source: "manual",
				},
			],
		});
		setManualForm({name: "", role: "", salary: ""});
		setShowManualForm(false);
	};

	const updateArrivalSalary = (id, salary) => {
		patchActive({
			arrivals: arrivals.map((a) =>
				a.id === id ? {...a, monthlySalary: Number(salary) || 0} : a,
			),
		});
	};

	const removeArrival = (id) => {
		patchActive({arrivals: arrivals.filter((a) => a.id !== id)});
	};

	// ── Header ────────────────────────────────────────────────────────────────
	const headerActions = [
		{
			icon: <AddIcon />,
			title: "Nuovo scenario",
			onClick: () => setShowNewModal(true),
		},
	];
	if (activeScenario) {
		headerActions.push({
			icon: <DeleteOutlineIcon />,
			title: "Elimina scenario",
			variant: "outlined",
			color: "error",
			onClick: () => setShowDeleteModal(true),
		});
	}

	const hasMoves = departures.length > 0 || arrivals.length > 0;
	const simulatedCount = players.length - departures.length + arrivals.length;
	const trendClass = (simulated, current) =>
		simulated < current
			? "market-simulation__down"
			: simulated > current
				? "market-simulation__up"
				: "";

	// ── Tables ────────────────────────────────────────────────────────────────
	const breakdownColumns = [
		{key: "groupLabel", label: "Reparto"},
		{key: "current", label: "Attuali", align: "right"},
		{
			key: "out",
			label: "Uscite",
			align: "right",
			render: (r) => (r.out > 0 ? "-" + r.out : "—"),
		},
		{
			key: "in",
			label: "Entrate",
			align: "right",
			render: (r) => (r.in > 0 ? "+" + r.in : "—"),
		},
		{
			key: "simulated",
			label: "Dopo il mercato",
			align: "right",
			render: (r) => (
				<span className={trendClass(r.simulated, r.current)}>
					{r.simulated}
				</span>
			),
		},
	];

	const departureColumns = [
		{
			key: "playerName",
			label: "Giocatore",
			render: (d) => (
				<div className="market-simulation__who">
					<span className="market-simulation__name">{d.playerName}</span>
					{d.monthlySalary > 0 && (
						<span className="market-simulation__meta">
							{fmtSalary(d.monthlySalary)} al mese
						</span>
					)}
				</div>
			),
		},
		{
			key: "reason",
			label: "Motivo",
			render: (d) => (
				<VLFSelectNew
					options={DEPARTURE_REASONS}
					value={DEPARTURE_REASONS.find((r) => r.value === d.reason) || null}
					onChange={(o) => setDepartureReason(d.playerId, o?.value)}
					sorted={false}
					isClearable={false}
					tableField
				/>
			),
		},
		{
			key: "remove",
			label: "",
			align: "right",
			render: (d) => (
				<MainActions
					actions={[
						{
							icon: <CloseIcon />,
							title: "Togli dalle uscite",
							variant: "outlined",
							onClick: () => removeDeparture(d.playerId),
						},
					]}
				/>
			),
		},
	];

	const arrivalColumns = [
		{
			key: "name",
			label: "Giocatore",
			render: (a) => (
				<div className="market-simulation__who">
					<span className="market-simulation__name">{a.name}</span>
					<span className="market-simulation__meta">{a.role}</span>
					{a.source === "scouting" && (
						<StatusBadge tone="neutral" label="Da scouting" />
					)}
				</div>
			),
		},
		{
			key: "monthlySalary",
			label: "Stipendio al mese (€)",
			render: (a) => (
				<VLFTextField
					type="number"
					placeholder="0"
					value={a.monthlySalary || ""}
					onChange={(e) => updateArrivalSalary(a.id, e.target.value)}
					margin="none"
				/>
			),
		},
		{
			key: "remove",
			label: "",
			align: "right",
			render: (a) => (
				<MainActions
					actions={[
						{
							icon: <CloseIcon />,
							title: "Togli dalle entrate",
							variant: "outlined",
							onClick: () => removeArrival(a.id),
						},
					]}
				/>
			),
		},
	];

	const prospectColumns = [
		{
			key: "name",
			label: "Giocatore",
			render: (p) => `${p.lastName || ""} ${p.firstName || ""}`,
		},
		{
			key: "role",
			label: "Ruolo",
			render: (p) => (p.goalkeeper ? "Portiere" : p.primaryRole || "—"),
		},
		{
			key: "added",
			label: "",
			align: "right",
			render: (p) =>
				arrivals.some((a) => a.prospectId === p.id) ? (
					<StatusBadge tone="good" label="Aggiunto" />
				) : null,
		},
	];

	// ─────────────────────────────────────────────────────────────────────────
	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				current="Simulazione mercato"
				actions={<MainActions actions={headerActions} />}
			/>
			<div className="market-simulation">
				<div className="market-simulation__notice">
					<StatusBadge
						tone="warning"
						label="Scenari salvati solo su questo computer finché il server non li gestisce"
					/>
				</div>

				{/* ── Scenari ─────────────────────────────────────────────────── */}
				{scenarios.length === 0 ? (
					<EmptyData
						action={
							<MainActions
								actions={[
									{
										icon: <AddIcon />,
										label: "Nuovo scenario",
										onClick: () => setShowNewModal(true),
									},
								]}
							/>
						}
					>
						Nessuno scenario: creane uno per provare entrate e uscite.
					</EmptyData>
				) : (
					<div className="market-simulation__scenarios">
						<ChoiceChips
							options={scenarioOptions}
							value={activeId}
							onChange={setActiveId}
						/>
						{activeScenario && (
							<span className="market-simulation__meta">
								{windowLabel(activeScenario.window)}
							</span>
						)}
					</div>
				)}

				{!activeScenario && scenarios.length > 0 && (
					<EmptyData>Scegli uno scenario.</EmptyData>
				)}

				{/* ── Contenuto ───────────────────────────────────────────────── */}
				{activeScenario && (
					<div className="market-simulation__layout">
						{/* Rosa attuale */}
						<div className="market-simulation__squad">
							<VLFCard
								title="Rosa attuale"
								content={
									!currentTeam ? (
										<EmptyData>Caricamento della rosa...</EmptyData>
									) : (
										<div className="market-simulation__stack">
											<div className="market-simulation__meta">
												{currentTeam.name || currentTeam.playerCategory?.name}
												{currentTeam.season?.name &&
													" · " + currentTeam.season.name}
												{" · " + players.length + " giocatori"}
												{avgAge && " · età media " + avgAge}
											</div>
											{expiringCount > 0 && (
												<StatusBadge
													tone="warning"
													label={
														expiringCount +
														(expiringCount === 1
															? " contratto scade"
															: " contratti scadono") +
														" entro 6 mesi"
													}
												/>
											)}
											<div className="market-simulation__meta">
												Tocca un giocatore per segnarlo in uscita.
											</div>
											{Object.entries(groupedPlayers).map(
												([group, members]) => (
													<div key={group} className="market-simulation__group">
														<div className="market-simulation__group-label">
															<span>{group}</span>
															<span>{members.length}</span>
														</div>
														{members
															.sort((a, b) =>
																(a.player?.lastName || "").localeCompare(
																	b.player?.lastName || "",
																),
															)
															.map((p) => {
																const pid = p.player?.id;
																const contract = contractsMap[pid];
																const isDep = departureSet.has(pid);
																const status = contractStatus(
																	contract?.endDate,
																);
																const months = contractMonths(
																	contract?.endDate,
																);
																const contractLabel = !contract?.endDate
																	? null
																	: isAlreadyExpired(contract)
																		? "Scaduto"
																		: months <= 24
																			? moment(contract.endDate).format("MM/YY")
																			: null;
																return (
																	<div
																		key={pid}
																		role="button"
																		tabIndex={0}
																		title={
																			isDep
																				? "Annulla uscita"
																				: "Segna come uscita"
																		}
																		className={
																			"market-simulation__player" +
																			(isDep ? " is-out" : "")
																		}
																		onClick={() => toggleDeparture(p)}
																	>
																		<div className="market-simulation__who">
																			<span className="market-simulation__name">
																				{p.player?.lastName}{" "}
																				<span className="market-simulation__first-name">
																					{p.player?.firstName}
																				</span>
																			</span>
																			{contract?.totalMonthlySalary > 0 && (
																				<span className="market-simulation__meta">
																					{fmtSalary(
																						contract.totalMonthlySalary,
																					)}{" "}
																					al mese
																				</span>
																			)}
																		</div>
																		<div className="market-simulation__player-aside">
																			{contractLabel && (
																				<StatusBadge
																					tone={
																						CONTRACT_TONES[status] || "neutral"
																					}
																					label={contractLabel}
																				/>
																			)}
																			{isDep && (
																				<StatusBadge
																					tone="critical"
																					label="In uscita"
																				/>
																			)}
																			{isDep ? (
																				<CloseIcon className="market-simulation__player-icon" />
																			) : (
																				<PersonRemoveIcon className="market-simulation__player-icon" />
																			)}
																		</div>
																	</div>
																);
															})}
													</div>
												),
											)}
										</div>
									)
								}
							/>
						</div>

						<div className="market-simulation__main">
							{/* Impatto */}
							<VLFCard
								title="Impatto del mercato"
								content={
									<KpiRow>
										<Kpi
											label="Stipendi attuali al mese"
											value={fmtSalary(currentMonthlySalary) || "—"}
										/>
										<Kpi
											label="Stipendi dopo il mercato"
											value={fmtSalary(simulatedMonthlySalary) || "—"}
											note={
												salaryDelta !== 0
													? (salaryDelta > 0 ? "+" : "") +
														fmtSalary(salaryDelta) +
														" al mese"
													: undefined
											}
											tone={
												salaryDelta > 0
													? "critical"
													: salaryDelta < 0
														? "good"
														: undefined
											}
										/>
										<Kpi
											label="Giocatori"
											value={players.length}
											note={
												hasMoves
													? "Dopo il mercato: " + simulatedCount
													: undefined
											}
										/>
										{departures.length > 0 && (
											<Kpi
												label="Uscite"
												value={"-" + departures.length}
												tone="critical"
											/>
										)}
										{arrivals.length > 0 && (
											<Kpi
												label="Entrate"
												value={"+" + arrivals.length}
												tone="good"
											/>
										)}
									</KpiRow>
								}
							/>

							{/* Composizione per ruolo */}
							{roleBreakdown.some((r) => r.out > 0 || r.in > 0) && (
								<VLFCard
									title="Composizione per ruolo"
									content={
										<DataTable
											columns={breakdownColumns}
											rows={roleBreakdown.map((r) => ({
												...r,
												id: r.groupLabel,
											}))}
										/>
									}
								/>
							)}

							{/* Uscite */}
							<VLFCard
								title={
									"Uscite" +
									(departures.length ? " · " + departures.length : "")
								}
								content={
									<DataTable
										columns={departureColumns}
										rows={departures.map((d) => ({...d, id: d.playerId}))}
										empty="Nessuna uscita: tocca un giocatore della rosa per segnarlo in uscita."
									/>
								}
							/>

							{/* Entrate */}
							<VLFCard
								title={
									"Entrate" + (arrivals.length ? " · " + arrivals.length : "")
								}
								content={
									<div className="market-simulation__stack">
										<div className="main-actions-bar">
											<MainActions
												actions={[
													{
														icon: <PersonAddIcon />,
														label: "Da scouting",
														onClick: () => setShowProspectModal(true),
													},
													{
														icon: <AddIcon />,
														label: "Aggiungi a mano",
														variant: "outlined",
														onClick: () => setShowManualForm((v) => !v),
													},
												]}
											/>
										</div>

										{showManualForm && (
											<div className="market-simulation__manual">
												<div>
													<VLFTextField
														label="Nome giocatore"
														value={manualForm.name}
														onChange={(e) =>
															setManualForm((f) => ({
																...f,
																name: e.target.value,
															}))
														}
														margin="none"
													/>
												</div>
												<div>
													<VLFSelectNew
														label="Ruolo"
														options={ROLE_OPTIONS}
														value={
															ROLE_OPTIONS.find(
																(o) => o.value === manualForm.role,
															) || null
														}
														onChange={(o) =>
															setManualForm((f) => ({
																...f,
																role: o?.value || "",
															}))
														}
														sorted={false}
														noMargin
													/>
												</div>
												<div>
													<VLFTextField
														label="Stipendio al mese (€)"
														type="number"
														placeholder="0"
														value={manualForm.salary}
														onChange={(e) =>
															setManualForm((f) => ({
																...f,
																salary: e.target.value,
															}))
														}
														margin="none"
													/>
												</div>
												<div className="market-simulation__manual-submit">
													<MainActions
														actions={[
															{
																label: "Aggiungi",
																onClick: addManualArrival,
																disabled: !manualForm.name || !manualForm.role,
															},
														]}
													/>
												</div>
											</div>
										)}

										<DataTable
											columns={arrivalColumns}
											rows={arrivals}
											empty="Nessuna entrata: aggiungi un giocatore dallo scouting o a mano."
										/>
									</div>
								}
							/>
						</div>
					</div>
				)}
			</div>

			{/* ── Modal: Nuovo scenario ──────────────────────────────────────── */}
			<VLFModal
				open={showNewModal}
				title="Nuovo scenario"
				content={
					<div className="market-simulation__form">
						<div>
							<VLFTextField
								label="Nome scenario"
								placeholder="es. Scenario A"
								value={newForm.name}
								onChange={(e) =>
									setNewForm((f) => ({...f, name: e.target.value}))
								}
								margin="none"
							/>
						</div>
						<div>
							<VLFSelectNew
								label="Rosa"
								options={teamOptions}
								value={
									teamOptions.find((o) => o.value === newForm.teamId) || null
								}
								onChange={(v) =>
									setNewForm((f) => ({...f, teamId: v?.value || ""}))
								}
								placeholder="Scegli una rosa..."
								noMargin
							/>
						</div>
						<div>
							<VLFSelectNew
								label="Finestra di mercato"
								options={WINDOWS}
								value={WINDOWS.find((w) => w.value === newForm.window) || null}
								onChange={(v) =>
									setNewForm((f) => ({...f, window: v?.value || ""}))
								}
								sorted={false}
								isClearable={false}
								noMargin
							/>
						</div>
					</div>
				}
				confirm={handleCreateScenario}
				close={closeNewModal}
				confirmLabel="Crea scenario"
			/>

			{/* ── Modal: Elimina scenario ────────────────────────────────────── */}
			<VLFModal
				open={showDeleteModal}
				title="Elimina scenario"
				content={
					<div className="market-simulation__confirm">
						Vuoi eliminare <strong>{activeScenario?.name}</strong>? Uscite ed
						entrate dello scenario andranno perse.
					</div>
				}
				confirm={handleDeleteScenario}
				close={() => setShowDeleteModal(false)}
				confirmLabel="Elimina"
			/>

			{/* ── Modal: Da scouting ────────────────────────────────────────── */}
			<VLFModal
				open={showProspectModal}
				title="Aggiungi dallo scouting"
				content={
					<div className="market-simulation__stack">
						<VLFTextField
							label="Cerca"
							placeholder="Cognome o nome"
							value={prospectSearch}
							onChange={(e) => {
								setProspectSearch(e.target.value);
								setProspectPage(1);
							}}
							margin="none"
							inputProps={{autoFocus: true}}
						/>

						{!prospectsLoading && (
							<div className="market-simulation__meta">
								{filteredProspects.length} giocatori
								{prospectSearch &&
									filteredProspects.length !== prospectPlayers.length &&
									" su " + prospectPlayers.length}
								{" · tocca un giocatore per aggiungerlo"}
							</div>
						)}

						{prospectsLoading ? (
							<div className="market-simulation__prospect-loading">
								<Loading visible />
							</div>
						) : (
							<DataTable
								columns={prospectColumns}
								rows={pagedProspects}
								onRowClick={addProspectArrival}
								empty="Nessun giocatore trovato."
							/>
						)}

						{prospectTotalPages > 1 && (
							<div className="market-simulation__pagination">
								<MainActions
									actions={[
										{
											icon: <ChevronLeftIcon />,
											title: "Pagina precedente",
											variant: "outlined",
											disabled: prospectPage <= 1 || prospectsLoading,
											onClick: () => setProspectPage((p) => p - 1),
										},
									]}
								/>
								<span className="market-simulation__meta">
									Pagina {prospectPage} di {prospectTotalPages}
								</span>
								<MainActions
									actions={[
										{
											icon: <ChevronRightIcon />,
											title: "Pagina successiva",
											variant: "outlined",
											disabled:
												prospectPage >= prospectTotalPages || prospectsLoading,
											onClick: () => setProspectPage((p) => p + 1),
										},
									]}
								/>
							</div>
						)}
					</div>
				}
				close={closeProspectModal}
			/>
		</Page>
	);
}

export default MarketSimulationPage;
